import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction } from "react-router-dom";

import { Chip } from "@material-tailwind/react";
import { format } from "date-fns";

import { archiveDeleteEligibility, CampaignSummary } from "@signalist/shared-types";

import { ReactComponent as ThreeDots } from "@assets/icons/three-dots.svg";
import SquareCheck from "@assets/icons/list-builder/square-check.svg?url";
import Square from "@assets/icons/list-builder/square.svg?url";

import { PermissionRoles } from "@/enum";
import { ListStatus } from "@/enum/list.enum";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { ContextMenu, MenuItem } from "@/components/ContextMenu/ContextMenu";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { TableColumn } from "@/components/Table/Table";
import { translateRequestError } from "@/utils/errors";

import { classNames } from "@/utils/classNames";
import { useRunListNowMutation } from "@/pages/Private/redux/list/list.api";
import { ROUTE_CONFIG } from "@/routes/config";

import { buildCampaignBuilderPath } from "../../List/utils/campaignBuilderPath";
import { List } from "../../List/schema/list";
import { EnrichmentCell, ListNameCell } from "../../List/components/listTableStatusCells";
import {
	CampaignMetricSubCell,
	CampaignStatusSubCell,
	CampaignSubRow,
} from "../../List/components/ListCampaignSubRows";
import {
	CampaignNameChannelSubCell,
	CampaignRowMenu,
	CampaignSelectSubCell,
	ListCampaignsCountPill,
} from "./CampaignSubRowCells";

interface UseCampaignsTableColumnsParams {
	notify: (message: string) => void;
	selectAll: boolean;
	toggleSelectAll: () => void;
	isListSelected: (listId: number) => boolean;
	toggleListSelection: (list: List) => void;
	isCampaignSelected: (listId: number, campaignId: string) => boolean;
	toggleCampaignSelection: (listId: number, campaign: CampaignSummary) => void;
	sortState: { sortBy: string; sortOrder: number };
	getSummaries: (listId: number) => CampaignSummary[] | undefined;
	handleToggleEnrichment: (id: number, status: ListStatus) => Promise<void>;
	toggleCampaign: (listId: number, campaign: CampaignSummary) => void;
	duplicateCampaign: (listId: number, campaign: CampaignSummary) => void;
	handleArchiveModal: (row: List) => void;
	navigate: NavigateFunction;
}

/**
 * Column definitions of the Campaigns overview table (PREC-3880). Same column skeleton as the
 * List overview (Robert's explicit intent: users should recognize the layout) with the campaign
 * sub-rows carrying the page's real content — selection, status pill, channel + name, and the
 * contacted / replied / positive metrics under the Open / Assigned / Last-added headers.
 */
export const useCampaignsTableColumns = ({
	notify,
	selectAll,
	toggleSelectAll,
	isListSelected,
	toggleListSelection,
	isCampaignSelected,
	toggleCampaignSelection,
	sortState,
	getSummaries,
	handleToggleEnrichment,
	toggleCampaign,
	duplicateCampaign,
	handleArchiveModal,
	navigate,
}: UseCampaignsTableColumnsParams): TableColumn[] => {
	const { t } = useTranslation();
	const [runListNow] = useRunListNowMutation();

	return useMemo(() => {
		const handleRunNow = async (id: number) => {
			try {
				await runListNow(id).unwrap();
			} catch (error) {
				notify(translateRequestError(error));
			}
		};
		const openCampaign = (listId: number, campaign: CampaignSummary) =>
			navigate(buildCampaignBuilderPath(listId, campaign));

		const columns: TableColumn[] = [
			{
				title: "",
				uid: "select",
				width: "52px",
				svgIcon: selectAll ? SquareCheck : Square,
				onHeaderClick: () => toggleSelectAll(),
				render: (row: List, index?: number) => (
					<div className="group flex items-center gap-2">
						<div
							className={classNames(
								"w-[24px] text-center font-medium text-table-header flex flex-row items-center justify-center gap-2",
								isListSelected(row.id) ? "hidden" : "group-hover:hidden"
							)}
						>
							{typeof index !== "undefined" ? index + 1 : ""}
						</div>
						<Checkbox
							className={classNames(isListSelected(row.id) ? "block" : "hidden group-hover:block")}
							isChecked={isListSelected(row.id)}
							name="selectedList"
							onChange={() => toggleListSelection(row)}
						/>
					</div>
				),
				renderSubRow: (sub: CampaignSubRow, parent: List) => (
					<CampaignSelectSubCell
						isSelected={"campaignId" in sub && isCampaignSelected(parent.id, sub.campaignId)}
						row={sub}
						onToggle={(campaign) => toggleCampaignSelection(parent.id, campaign)}
					/>
				),
			},
			{
				title: "",
				uid: "expand",
				width: "36px",
				isExpandToggle: true,
				render: () => <></>,
			},
			{
				title: t("list.enrichment.title"),
				width: "210px",
				maxWidth: "210px",
				orderTitle: "status",
				uid: "status",
				orderStatus: sortState.sortBy === "status" ? sortState.sortOrder : 0,
				render: (row: List) => (
					<EnrichmentCell list={row} onRunNow={handleRunNow} onToggle={handleToggleEnrichment} />
				),
			},
			{
				title: t("list.status.campaignStatus"),
				width: "160px",
				uid: "campaignStatus",
				render: (row: List) => (
					<ListCampaignsCountPill
						campaignCount={Number(row.campaignCount ?? 0)}
						summaries={getSummaries(row.id)}
					/>
				),
				renderSubRow: (sub: CampaignSubRow, parent: List) => (
					<CampaignStatusSubCell
						row={sub}
						onToggle={(campaign) => toggleCampaign(parent.id, campaign)}
					/>
				),
			},
			{
				title: t("list.name"),
				minWidth: "240px",
				orderTitle: "name",
				orderStatus: sortState.sortBy === "name" ? sortState.sortOrder : 0,
				uid: "name",
				render: (row: List) => <ListNameCell isSequencerActive list={row} />,
				renderSubRow: (sub: CampaignSubRow, parent: List) => (
					<CampaignNameChannelSubCell
						row={sub}
						onOpen={(campaign) => openCampaign(parent.id, campaign)}
					/>
				),
			},
			{
				title: t("list.newLeads"),
				width: "112px",
				uid: "newLeads",
				render: () => <></>,
				renderSubRow: (sub: CampaignSubRow) => (
					<CampaignMetricSubCell metric="contacted" row={sub} />
				),
			},
			{
				title: t("list.assignedLeads"),
				width: "112px",
				uid: "assignedLeads",
				render: (row: List) => (
					<div className="flex">
						{Number(row.assignedLeads ?? 0) > 0 ? (
							<Chip
								className="bg-brand-light"
								color="blue"
								size="sm"
								value={`${row.assignedLeads}`}
							/>
						) : (
							""
						)}
					</div>
				),
				renderSubRow: (sub: CampaignSubRow) => <CampaignMetricSubCell metric="replied" row={sub} />,
			},
			{
				title: t("list.lastAddedAt"),
				width: "140px",
				orderTitle: "lastAddedAt",
				orderStatus: sortState.sortBy === "lastAddedAt" ? sortState.sortOrder : 0,
				uid: "lastAddedAt",
				render: (row: List) => (
					<div className="flex">
						<div className="text-sm">
							{row?.lastAddedAt && format(new Date(row.lastAddedAt), "yyyy-MM-dd HH:mm")}
						</div>
					</div>
				),
				renderSubRow: (sub: CampaignSubRow) => (
					<CampaignMetricSubCell metric="positive" row={sub} />
				),
			},
			{
				title: "",
				width: "56px",
				maxWidth: "56px",
				uid: "menu",
				render: (row: List) => {
					const eligibility = archiveDeleteEligibility(row);
					const disabledReason = eligibility.allowed
						? undefined
						: t({ LIST_IS_PROCESSING: "errors.listIsProcessing" }[eligibility.reason]);

					const options: MenuItem[] = [
						{
							title: t("campaignsOverview.rowMenu.openList"),
							onClick: () => navigate(`${ROUTE_CONFIG.LISTS}/detail/${row.id}?tab=UNASSIGNED`),
						},
						{
							title: t("campaignsOverview.rowMenu.newCampaignForList"),
							onClick: () =>
								navigate(`${ROUTE_CONFIG.LISTS}/create/copywriting/${row.id}?createCampaign=true`),
						},
						{
							title: row.archived
								? t("list.unarchiveList")
								: t("campaignsOverview.rowMenu.archiveList"),
							permissions: [PermissionRoles.LIST],
							// PREC-4537: the overview's archive had no gate; it now reads the API's decision.
							disabled: !eligibility.allowed,
							disabledReason,
							onClick: () => handleArchiveModal(row),
						},
					];

					return (
						<div className="flex justify-end gap-2">
							<ContextMenu data={[options]} position="bottom-right" width="190">
								<SvgIcon
									className="w-[20px] h-[20px] text-gray-700 cursor-pointer"
									svgIcon={ThreeDots}
								/>
							</ContextMenu>
						</div>
					);
				},
				renderSubRow: (sub: CampaignSubRow, parent: List) => (
					<CampaignRowMenu
						listId={Number(parent.id)}
						row={sub}
						onDuplicate={(campaign) => duplicateCampaign(parent.id, campaign)}
						onOpen={(campaign) => openCampaign(parent.id, campaign)}
						onToggleStatus={(campaign) => toggleCampaign(parent.id, campaign)}
					/>
				),
			},
		];

		return columns;
	}, [
		t,
		selectAll,
		toggleSelectAll,
		isListSelected,
		toggleListSelection,
		isCampaignSelected,
		toggleCampaignSelection,
		sortState,
		getSummaries,
		handleToggleEnrichment,
		toggleCampaign,
		duplicateCampaign,
		handleArchiveModal,
		navigate,
		runListNow,
		notify,
	]);
};
